import { Fragment, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from 'react-query';

import { merchantsApi, shopsApi, trustedCustomersApi } from 'api';
import {
  CloseDialogResult,
  CrudPage,
  DataGridColumnDefinition,
  dataGridColumns,
  Dialog,
  UserInfo,
} from 'components';
import { FilterDefinitionType, QueryKey } from 'enums';
import { useMutation, usePartialQuery, useShopsQuery, useUser } from 'hooks';
import { TranslationNamespace } from 'i18n';
import {
  FilterDefinition,
  TrustedCustomer,
  TrustedCustomersFilters,
} from 'types';
import { formatUtils } from 'utils';

export const TrustedCustomersPage: React.FC = () => {
  const { t: tLayout } = useTranslation(TranslationNamespace.Admin, {
    keyPrefix: 'layout',
  });
  const { t } = useTranslation(TranslationNamespace.Admin, {
    keyPrefix: 'features.trusted_customers',
  });
  const { t: tCommon } = useTranslation();
  const { role } = useUser();
  const queryClient = useQueryClient();

  const queryResult = usePartialQuery(
    QueryKey.TrustedCustomers,
    trustedCustomersApi.getAllPaginated,
  );

  const [confirmRemoveDialogProps, setConfirmRemoveDialogProps] = useState<{
    open: boolean;
    data?: TrustedCustomer;
  }>({
    open: false,
  });

  const queryResultMerchants = useQuery(
    QueryKey.Merchants,
    merchantsApi.getAllAsRole(role),
  );

  const queryResultShops = useShopsQuery(
    QueryKey.Shops,
    shopsApi.getAllAsRole(role),
  );

  const { mutate: removeTrustedCustomer, isLoading: isRemoveLoading } =
    useMutation(trustedCustomersApi.remove, {
      onSuccess: () => {
        setConfirmRemoveDialogProps({ open: false });
        queryClient.invalidateQueries(QueryKey.TrustedCustomers);
      },
    });

  const handleCloseRemoveDialog = useCallback(
    ({ ok, data }: CloseDialogResult<TrustedCustomer>) => {
      if (ok && data) {
        removeTrustedCustomer(data.id);
      } else {
        setConfirmRemoveDialogProps({ open: false });
      }
    },
    [removeTrustedCustomer],
  );

  const handleRemove = useCallback((data: TrustedCustomer) => {
    setConfirmRemoveDialogProps({ open: true, data });
  }, []);

  const columns = useMemo(
    (): DataGridColumnDefinition<TrustedCustomer>[] => [
      dataGridColumns.getIdColumn(),
      {
        header: t('fields.shop'),
        valueGetter: (item) => (
          <Fragment>
            <div>{item.merchant?.user.name}</div>
            <div>{formatUtils.formatName(item.shop)}</div>
          </Fragment>
        ),
      },
      {
        header: t('fields.customer'),
        valueGetter: (item) => <UserInfo id={item.customerId} />,
      },
      {
        header: t('fields.last_completed_order_at'),
        valueKey: 'lastCompletedOrderAt',
        valueFormatter: formatUtils.formatDate,
      },
      dataGridColumns.getActionsColumn({
        handleRemove,
      }),
    ],
    [t, handleRemove],
  );

  const filtersDefinitions: FilterDefinition<TrustedCustomersFilters>[] =
    useMemo(
      () => [
        {
          label: tCommon('filters.merchant'),
          name: 'merchantId',
          type: FilterDefinitionType.Merchant,
          merchants: queryResultMerchants.data,
        },
        {
          label: tCommon('filters.shop'),
          name: 'shopId',
          type: FilterDefinitionType.Shop,
          shops: queryResultShops.data,
        },
        {
          label: tCommon('filters.customer'),
          name: 'customerId',
          type: FilterDefinitionType.Text,
        },
      ],
      [queryResultMerchants.data, queryResultShops.data, tCommon],
    );

  return (
    <Fragment>
      <CrudPage
        header={{ title: tLayout('menu_items.customers') }}
        filters={{ filtersDefinitions }}
        table={{ queryResult, columns, paginated: true }}
      >
        <Dialog
          title={t('remove_dialog.title')}
          onClose={handleCloseRemoveDialog}
          disabled={isRemoveLoading}
          {...confirmRemoveDialogProps}
        >
          <Fragment>
            <div className="tw-mb-4">{t('remove_dialog.description')}</div>
            {confirmRemoveDialogProps.data && (
              <Fragment>
                <div className="tw-mb-2">
                  <div>{confirmRemoveDialogProps.data.merchant.user.name}</div>
                  <div>
                    {formatUtils.formatName(confirmRemoveDialogProps.data.shop)}
                  </div>
                </div>
                <div>
                  {confirmRemoveDialogProps.data.customerId && (
                    <div>
                      {`${t('fields.customer')}: `}
                      <UserInfo id={confirmRemoveDialogProps.data.customerId} />
                    </div>
                  )}
                  <div>{`${t(
                    'fields.last_completed_order_at',
                  )}: ${formatUtils.formatDate(
                    confirmRemoveDialogProps.data.lastCompletedOrderAt,
                  )}`}</div>
                </div>
              </Fragment>
            )}
          </Fragment>
        </Dialog>
      </CrudPage>
    </Fragment>
  );
};
